import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, Row, Col, Form, Collapse } from "react-bootstrap";
import { useParams, Link } from "react-router-dom";
import { QUESTIONLIST, QUESTIONFORM, BACKTOLIST } from "../../../../shared/constants/Constants";
import DefaultButton from "../../../../shared/components/DefaultButton";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import AddQuestions from "./AddQuestion";
import { loadQuestionTemplateFormAddDataRequest, loadQuestionTemplateFormUpdateDataRequest, loadQuestionTemplateDetailsDataRequest, resetQuestionState } from "../redux/Actions";


const QuestionForm = () => {
  const { id } = useParams(); // edit mode
  const isEditMode = id && !isNaN(Number(id));
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [questions, setQuestions] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasInitialized = React.useRef(false);
  const { addSuccess, updateSuccess, question } = useSelector((state) => state.question);
  const [showCategories, setShowCategories] = useState(false);
  const shouldExitRef = React.useRef(true);

  useEffect(() => {
    dispatch(resetQuestionState());
  }, [dispatch]);

  useEffect(() => {
    if (addSuccess || updateSuccess) {
      dispatch(resetQuestionState());
      if (shouldExitRef.current) {
        navigate("/questionlist");
      } else {
        // Stay on page — re-fetch fresh data
        hasInitialized.current = false;
        dispatch(loadQuestionTemplateDetailsDataRequest(Number(id)));
      }
    }
  }, [addSuccess, updateSuccess, navigate, dispatch, id]);
  
  useEffect(() => {
    if (isEditMode) {
      // Reset questions as soon as we detect edit mode
      setQuestions([]);
      setTemplateName("");
      setTemplateDesc("");
      hasInitialized.current = false;

      dispatch(loadQuestionTemplateDetailsDataRequest(Number(id)));
    }
  }, [dispatch, isEditMode, id]);

  useEffect(() => {
    if (isEditMode && question?.data && !hasInitialized.current) {
      const data = question.data;

      // IMPORTANT: Only set data if it matches the current ID
      if (data.id === Number(id)) {
        setTemplateName(data.template_name || "");
        setTemplateDesc(data.template_description || "");

        const mappedQuestions = (data.questions || [])
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((q) => ({
            id: q.id,
            name: q.questionText,
            answerType: q.answerType,
            answerValidation: q.answerValidation || "",
            required: q.isRequired,
            is_active: q.isActive,
            order: q.displayOrder,
          }));

        setQuestions(mappedQuestions);
        hasInitialized.current = true;
      }
    }
  }, [isEditMode, question, id]);




  // Save Template with questions
  const handleSaveTemplate = (shouldExit = true) => {
    shouldExitRef.current = shouldExit;
    const payload = {
      templateName,
      templateDescription: templateDesc,
      questions: questions.map((q, index) => ({
        id: q.id || null,
        questionText: q.name,
        answerType: q.answerType,
        answerValidation: q.answerValidation || "",
        isRequired: q.required,
        displayOrder: q.order ?? index + 1,
        isActive: q.is_active
      }))
    };

    if (isEditMode) {
      dispatch(loadQuestionTemplateFormUpdateDataRequest(Number(id), payload, shouldExit));
    } else {
      dispatch(loadQuestionTemplateFormAddDataRequest(payload, shouldExit));
    }
  };
  
  const handleCancel = () => {
    if (isEditMode) {
      navigate("/questionlist");
    } else {
      setTemplateName("");
      setTemplateDesc("");
      setQuestions([]);
    }
  };




  return (
    <>
      <Row className="detailsPageHeader mb-0">
        <Col>
          <Breadcrumbs
            children={[
              { href: "/questionlist", label: QUESTIONLIST },
              { href: "/questionform", label: question?.data?.template_name || QUESTIONFORM }
            ]}
          />
        </Col>
        <Col className="text-end">
          <Link to="/questionlist">
            <DefaultButton buttonClass="default-btn-color" buttonTitle={BACKTOLIST} />
          </Link>
        </Col>
      </Row>

      <Card className="p-4">
        {/* ================= TEMPLATE DETAILS ================= */}
        
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>
               <strong> Template Name</strong> <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
               <strong>Template Description</strong> 
              </Form.Label>
              <Form.Control
                value={templateDesc}
                onChange={(e) => setTemplateDesc(e.target.value)}
                placeholder="Optional description"
              />
            </Form.Group>
          </Col>
        </Row>    
        
        {/* ================= CATEGORY QUESTIONS ================= */}
        <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="py-2">         
        <AddQuestions
          values={{ questions }}
          setFieldValue={(field, value) => {
            if (field === "questions") setQuestions(value);
          }}
          isEditMode={!!id}
        />
        </Card.Body>
        </Card>
        {/* ================= TEMPLATE CATEGORIES ================= */}
          {isEditMode && question?.data?.categories?.length > 0 && (
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="py-2">                  
                  {/* Header */}
                  <div
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowCategories((prev) => !prev)}
                  >
                    <div >
                      Associated Categories
                      <span className="text-muted ms-2">
                        ({question.data.categories.length})
                      </span>
                    </div>

                    <i
                      className={`fas fa-chevron-${showCategories ? "up" : "down"}`}
                    />
                  </div>

                  {/* Collapsible content */}
                  <Collapse in={showCategories} unmountOnExit>
                    <div>
                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {question.data.categories.map((category) => (
                          <Link
                            key={category.id}
                            to={`/categoryform/${category.id}`}
                            className="category-pill gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {category.name}
                            <i  className="fas fa-external-link-alt location-link-icon"/>
                                     </Link>
                        ))}
                      </div>
                    </div>
                  </Collapse>

                </Card.Body>
              </Card>
            )}    

        {/* ================= SAVE TEMPLATE ================= */}
        <div className="mt-4 text-end d-flex justify-content-end gap-2">
          <DefaultButton
            buttonTitle="Cancel"
            buttonClass="btn-secondary"
            actionBlock={handleCancel}
          />

          <DefaultButton
            buttonTitle="Save"
            buttonClass="default-btn-color"
            disabled={!templateName || questions.length === 0}
            actionBlock={() => handleSaveTemplate(false)}
          />

          <DefaultButton
            buttonTitle="Save & Exit"
            buttonClass="default-btn-color"
            disabled={!templateName || questions.length === 0}
            actionBlock={() => handleSaveTemplate(true)}
          />
        </div>
              </Card>
    </>
  );
};

export default QuestionForm;
